/**
 * Posture attribute catalog (VSNP-1174; corrected for the release in VSNP-1957).
 *
 * The posture facts a policy can match: what each release client really
 * reports in its signed posture document, plus the facts the gateway sets
 * itself (mtls, token_verified, token_bound). It drives the requirements
 * editor, so an admin picks a known attribute with a typed value instead of
 * free-texting a key no client sends (an absent fact never matches), and it
 * drives the warnings for requirements some platforms can never meet.
 *
 * Source of truth: the client code, not the plans (VSNP-1957 step 0, doc 15b):
 *   - macOS / iOS: SecureLinkVPN-Apple AccessShared/AccessPosture.swift. macOS
 *     reports disk_encryption, device_integrity and edr as unknown (FileVault is
 *     not readable from the sandbox); iOS reports disk_encryption with the
 *     passcode (data protection).
 *   - Android: SecureLinkVPN-Android core/.../access/PostureFacts.kt (N3).
 *   - Linux CLI: access-client core/posture/hostchecks_linux.go (LUKS, GNOME
 *     screen lock, Secure Boot, known EDR agents); its device key is in
 *     software, so hardware_backed is false (core/client/loop.go).
 *   - Windows: no Secure Access engine yet (SecureLinkVPN-Windows W1 is plane
 *     plumbing only). The core's Windows checks exist (hostchecks_windows.go)
 *     and are what the engine will report once it ships.
 *   - The `os` fact is the Go platform name (runtime.GOOS): a Mac reports
 *     `darwin`, not `macos`.
 *   - Gateway: internal/router AuthzInputFor and internal/l3gw set session_kind,
 *     token_bound, token_verified, mtls; internal/policy matches any key, an
 *     absent or empty fact fails closed, `unknown` is an ordinary value.
 *
 * Keep it in sync with those files when a client changes what it measures.
 */

import type { PostureOp, PosturePredicate } from '../services/access.service';

export type PostureValueType = 'enum' | 'bool' | 'version' | 'string';

/** A client platform, as the API names it (access_devices.platform). */
export type ClientPlatform = 'macos' | 'ios' | 'windows' | 'android' | 'linux';

export const CLIENT_PLATFORMS: ClientPlatform[] = ['macos', 'ios', 'windows', 'android', 'linux'];

export const PLATFORM_LABEL: Record<ClientPlatform, string> = {
  macos: 'macOS',
  ios: 'iOS',
  windows: 'Windows',
  android: 'Android',
  linux: 'Linux (CLI)',
};

/** What the devices of a platform are called in a sentence ("Macs report …"). */
const PLATFORM_DEVICES: Record<ClientPlatform, string> = {
  macos: 'Macs',
  ios: 'iPhones and iPads',
  windows: 'Windows PCs',
  android: 'Android devices',
  linux: 'Linux CLI devices',
};

/**
 * Platforms whose Secure Access engine is not built yet: no device of theirs can
 * enrol, so they are shown as "not available" and left out of the warnings.
 * Remove a platform when its engine ships, and fill its `reports` below.
 */
export const PLATFORMS_WITHOUT_ENGINE: ClientPlatform[] = ['windows'];

/**
 * MDM-backed and hardware-attested posture (VSNP-1169 / VSNP-1170). Off for this
 * release (owner decision 2026-10-26): `device_integrity` and the `source` fact
 * (self / attested / mdm) are not offered. Flip to true when those ship.
 */
export const MDM_AND_ATTESTATION_POSTURE = false;

/**
 * What one platform's client reports for a fact:
 *   - 'measured': a real reading (pass / fail, or a real value);
 *   - { always: v }: it always reports v (e.g. 'unknown', or 'false').
 * A platform without an entry has no Secure Access engine yet.
 */
export type PlatformReport = 'measured' | { always: string };

export interface PostureAttribute {
  key: string;
  label: string;
  /** The fact in a sentence ("Macs report unknown for disk encryption"); default: the label, lower case. */
  noun?: string;
  description: string;
  valueType: PostureValueType;
  /** For enum/bool: the values the admin may pick. */
  allowedValues?: string[];
  /** Display names for allowed values (e.g. darwin → macOS). */
  valueLabels?: Record<string, string>;
  /** Who sets the fact: the device's signed self-report, or the gateway itself. */
  origin: 'device' | 'gateway';
  /** Device facts only: what each release platform reports. */
  reports?: Partial<Record<ClientPlatform, PlatformReport>>;
  /** Platforms that really measure this fact (derived from `reports`). */
  platforms: ClientPlatform[];
  /** True when the orchestrator can vouch for this fact per-device (VSNP-1168). */
  serverVouchable: boolean;
  /** False when no release platform measures it (it would always be unknown). */
  measured: boolean;
  /** Not offered in this release (behind MDM_AND_ATTESTATION_POSTURE). */
  hidden?: boolean;
  /** Skip the per-platform "never passes" warnings (e.g. `os`, which is meant to exclude). */
  noPlatformWarnings?: boolean;
  /** Optional caveat shown under the row. */
  note?: string;
}

const TRISTATE = ['pass', 'fail', 'unknown'];
const UNKNOWN: PlatformReport = { always: 'unknown' };

type AttributeSpec = Omit<PostureAttribute, 'platforms' | 'measured' | 'hidden'> & { gate?: 'mdm-attestation' };

const SPECS: AttributeSpec[] = [
  {
    key: 'disk_encryption',
    noun: 'disk encryption',
    label: 'Disk encryption',
    description: 'The system volume or the device storage is encrypted at rest.',
    valueType: 'enum',
    allowedValues: TRISTATE,
    origin: 'device',
    reports: { macos: UNKNOWN, ios: 'measured', android: 'measured', linux: 'measured' },
    serverVouchable: true,
    note:
      'iOS: on when a passcode is set (data protection). Android: storage encryption status. ' +
      'Linux: a LUKS layer under /. macOS cannot read FileVault from the App Store sandbox and reports unknown.',
  },
  {
    key: 'screen_lock',
    noun: 'screen lock',
    label: 'Screen lock',
    description: 'A credential-protected lock is set on the device.',
    valueType: 'enum',
    allowedValues: TRISTATE,
    origin: 'device',
    reports: { macos: 'measured', ios: 'measured', android: 'measured', linux: 'measured' },
    serverVouchable: true,
    note:
      'macOS / iOS: a password or passcode is set. Android: a PIN, pattern or password. ' +
      "Linux: GNOME's lock setting as the service sees it; unknown on servers and other desktops.",
  },
  {
    key: 'edr',
    noun: 'EDR',
    label: 'EDR present',
    description: 'A recognised endpoint-detection agent is running.',
    valueType: 'enum',
    allowedValues: TRISTATE,
    origin: 'device',
    reports: { macos: UNKNOWN, ios: UNKNOWN, android: UNKNOWN, linux: 'measured' },
    serverVouchable: true,
    note:
      'Linux only: pass when CrowdStrike, SentinelOne, Defender for Endpoint, Trend Micro, Carbon Black or ' +
      'Cortex XDR is running, otherwise unknown (never fail). The apps report unknown.',
  },
  {
    key: 'hardware_backed',
    noun: 'the hardware-backed key',
    label: 'Hardware-backed device key',
    description: 'The device key (mTLS, DPoP, enrolment) is held by hardware: Secure Enclave, StrongBox / TEE.',
    valueType: 'bool',
    allowedValues: ['true', 'false'],
    origin: 'device',
    reports: { macos: 'measured', ios: 'measured', android: 'measured', linux: { always: 'false' } },
    serverVouchable: false,
    note:
      'Informational: self-reported in the signed posture document. The Linux CLI keeps its key in software ' +
      'and reports false.',
  },
  {
    key: 'os',
    label: 'Operating system',
    description: 'The device platform, as the client names it (Go platform names).',
    valueType: 'enum',
    allowedValues: ['darwin', 'ios', 'android', 'linux', 'windows'],
    valueLabels: { darwin: 'macOS (darwin)', ios: 'iOS', android: 'Android', linux: 'Linux', windows: 'Windows' },
    origin: 'device',
    reports: { macos: 'measured', ios: 'measured', android: 'measured', linux: 'measured' },
    serverVouchable: false,
    noPlatformWarnings: true,
    note: 'A Mac reports darwin, not macos.',
  },
  {
    key: 'os_version',
    label: 'OS version',
    description: 'Operating-system version (supports ≥ / ≤ comparisons, e.g. ≥ 14).',
    valueType: 'version',
    origin: 'device',
    reports: { macos: 'measured', ios: 'measured', android: 'measured', linux: 'measured' },
    serverVouchable: false,
    noPlatformWarnings: true,
    note: 'Each platform numbers its own versions (macOS 26.5, Android 14, Ubuntu 24.04): pair a version with an OS.',
  },
  {
    key: 'device_integrity',
    noun: 'device integrity',
    label: 'Device integrity',
    description: 'Boot chain / OS integrity (Secure Boot; hardware attestation when available).',
    valueType: 'enum',
    allowedValues: TRISTATE,
    origin: 'device',
    reports: { macos: UNKNOWN, ios: UNKNOWN, android: UNKNOWN, linux: 'measured' },
    serverVouchable: true,
    gate: 'mdm-attestation',
    note: 'It needs attestation or MDM to mean anything. The apps report unknown; the Linux CLI reports Secure Boot.',
  },
  {
    key: 'source',
    label: 'Posture source',
    description: 'Provenance of the posture: self-reported, hardware-attested, or MDM-vouched.',
    valueType: 'enum',
    allowedValues: ['self', 'attested', 'mdm'],
    origin: 'gateway',
    serverVouchable: false,
    gate: 'mdm-attestation',
    note: 'It needs MDM or attestation; without them every device is self.',
  },
  {
    key: 'mtls',
    label: 'Client certificate (mTLS)',
    description: 'The device presented its enrolled client certificate.',
    valueType: 'bool',
    allowedValues: ['true', 'false'],
    origin: 'gateway',
    serverVouchable: false,
  },
  {
    key: 'token_verified',
    label: 'Identity verified (IdP)',
    description: 'The session carries an IdP-verified identity token.',
    valueType: 'bool',
    allowedValues: ['true', 'false'],
    origin: 'gateway',
    serverVouchable: false,
  },
  {
    key: 'token_bound',
    label: 'Token bound to device (DPoP)',
    description: "The access token is bound to the device's key (DPoP), so a copied token is useless elsewhere.",
    valueType: 'bool',
    allowedValues: ['true', 'false'],
    origin: 'gateway',
    serverVouchable: false,
  },
];

function build(spec: AttributeSpec): PostureAttribute {
  const { gate, ...attr } = spec;
  const platforms = CLIENT_PLATFORMS.filter((p) => attr.reports?.[p] === 'measured');
  return {
    ...attr,
    platforms,
    measured: attr.origin === 'gateway' || platforms.length > 0,
    hidden: gate === 'mdm-attestation' && !MDM_AND_ATTESTATION_POSTURE,
  };
}

/** Every attribute the catalog knows, offered or not (labels for existing requirements). */
export const ALL_POSTURE_ATTRIBUTES: PostureAttribute[] = SPECS.map(build);

/** The attributes an admin may pick in this release. */
export const POSTURE_CATALOG: PostureAttribute[] = ALL_POSTURE_ATTRIBUTES.filter((a) => !a.hidden);

/** Look up a catalog attribute by key (offered or not). */
export function postureAttribute(key: string): PostureAttribute | undefined {
  return ALL_POSTURE_ATTRIBUTES.find((a) => a.key === key);
}

/**
 * Checks the gateway and the server make on every session, whatever the profile
 * says. Not profile requirements: shown so an admin knows what is already
 * verified without the device's word.
 */
export const PLATFORM_CHECKS: { label: string; detail: string }[] = [
  { label: 'Client certificate (mTLS)', detail: "The device's enrolled certificate, current serial, not revoked." },
  { label: 'Device approved', detail: 'Only devices on the approved roster are admitted; pending, retired and deleted ones are refused.' },
  { label: 'Identity verified (IdP)', detail: 'The user signed in through the identity provider; rules can require it (token_verified).' },
  { label: 'Token bound to device (DPoP)', detail: "The token is bound to the device's key; rules can require it (token_bound)." },
  { label: 'Minimum client version', detail: "Set per platform by the operator; an older app is refused at sign-in, enrolment and connect." },
];

/** The operators that make sense for a value type. */
export function opsForType(t: PostureValueType): PostureOp[] {
  switch (t) {
    case 'enum':
    case 'string':
      return ['eq', 'neq', 'in', 'not_in'];
    case 'version':
      return ['eq', 'neq', 'gte', 'lte', 'gt', 'lt'];
    case 'bool':
      return ['eq'];
  }
}

/** A synthetic attribute for a key not in the catalog (e.g. a legacy rule),
 *  so editing never drops it. Treated as a free string. */
export function unknownAttribute(key: string): PostureAttribute {
  return {
    key,
    label: key,
    description: 'Custom attribute (not in the catalog).',
    valueType: 'string',
    origin: 'device',
    platforms: [],
    serverVouchable: false,
    measured: false,
    note: 'This key is not a known collected fact — it may never match.',
  };
}

/** The display name of a value (e.g. darwin → "macOS (darwin)"). */
export function valueLabel(attr: PostureAttribute, value: string): string {
  return attr.valueLabels?.[value] ?? value;
}

/** What a platform reports for an attribute: 'measured', a fixed value, or null (no engine yet). */
export function platformReport(attr: PostureAttribute, platform: ClientPlatform): PlatformReport | null {
  if (PLATFORMS_WITHOUT_ENGINE.includes(platform)) return null;
  return attr.reports?.[platform] ?? null;
}

/** A device fact's platforms in three groups: measured, a fixed value, no engine yet. */
export function platformSummary(attr: PostureAttribute): {
  measured: ClientPlatform[];
  fixed: Array<{ platform: ClientPlatform; value: string }>;
  notAvailable: ClientPlatform[];
} {
  const measured: ClientPlatform[] = [];
  const fixed: Array<{ platform: ClientPlatform; value: string }> = [];
  const notAvailable: ClientPlatform[] = [];
  for (const p of CLIENT_PLATFORMS) {
    const r = platformReport(attr, p);
    if (r === null) notAvailable.push(p);
    else if (r === 'measured') measured.push(p);
    else fixed.push({ platform: p, value: r.always });
  }
  return { measured, fixed, notAvailable };
}

/**
 * Would a device reporting `have` meet `pred`? The gateway's rule
 * (internal/policy postureOpMatch): a bare string is equality, `unknown` is an
 * ordinary value (it equals only "unknown"), an empty value fails closed.
 */
export function predicateAccepts(pred: PosturePredicate, have: string): boolean {
  if (!have) return false;
  if (typeof pred === 'string') return have === pred;
  const values = pred.values ?? [];
  switch (pred.op) {
    case 'eq':
      return values.length === 1 && have === values[0];
    case 'neq':
      return values.length === 1 && have !== values[0];
    case 'in':
      return values.includes(have);
    case 'not_in':
      return values.length > 0 && !values.includes(have);
    default:
      return false; // version comparisons are not judged here
  }
}

/** One platform that can never meet a requirement, and why. */
export interface PlatformShortfall {
  platform: ClientPlatform;
  /** The value that platform always reports for the fact. */
  reports: string;
  message: string;
}

/** The fact in a sentence. */
export function nounOf(attr: PostureAttribute): string {
  return attr.noun ?? attr.label.toLowerCase();
}

/** The values a requirement names (bare string or clause). */
function predicateValues(pred: PosturePredicate): string[] {
  return typeof pred === 'string' ? [pred] : pred.values ?? [];
}

/**
 * The platforms that can never meet `key pred`: they always report a value the
 * requirement rejects (unknown on a Mac for disk_encryption, false on the Linux
 * CLI for hardware_backed). Empty for gateway facts, custom keys, platforms
 * without an engine, and attributes meant to exclude (os).
 */
export function requirementShortfalls(key: string, pred: PosturePredicate): PlatformShortfall[] {
  const attr = postureAttribute(key);
  if (!attr || attr.origin !== 'device' || attr.noPlatformWarnings) return [];
  const out: PlatformShortfall[] = [];
  for (const p of CLIENT_PLATFORMS) {
    const r = platformReport(attr, p);
    if (!r || r === 'measured') continue;
    if (predicateAccepts(pred, r.always)) continue;
    out.push({
      platform: p,
      reports: r.always,
      message: `${PLATFORM_DEVICES[p]} report ${r.always} for ${nounOf(attr)} and will never pass.`,
    });
  }
  return out;
}

/** Notes on a requirement that are not per platform. */
export function requirementNotes(key: string, pred: PosturePredicate): string[] {
  const attr = postureAttribute(key);
  if (!attr) return [];
  const notes: string[] = [];
  if (attr.hidden) notes.push(`${attr.label} is not offered in this release. ${attr.note ?? ''}`.trim());
  if (attr.allowedValues && attr.valueType !== 'version') {
    const strange = predicateValues(pred).filter((v) => !attr.allowedValues!.includes(v));
    for (const v of strange) {
      notes.push(
        key === 'os' && v === 'macos'
          ? 'No client reports os = macos: Macs report darwin.'
          : `No client reports ${nounOf(attr)} = ${v}.`,
      );
    }
  }
  // "is not fail" / "not in {fail}" also admits devices that report unknown.
  if (attr.allowedValues?.includes('unknown') && typeof pred !== 'string' && predicateAccepts(pred, 'unknown')) {
    notes.push(`Devices that report unknown for ${nounOf(attr)} also meet this requirement.`);
  }
  return notes;
}

/** Everything worth warning about for a set of requirements (a profile). */
export function profileWarnings(requirements: Record<string, PosturePredicate> | null | undefined): {
  shortfalls: Array<PlatformShortfall & { key: string }>;
  notes: string[];
  /** The platforms (with an engine) that can never meet the whole profile. */
  neverPass: ClientPlatform[];
} {
  const shortfalls: Array<PlatformShortfall & { key: string }> = [];
  const notes: string[] = [];
  for (const [key, pred] of Object.entries(requirements ?? {})) {
    for (const s of requirementShortfalls(key, pred)) shortfalls.push({ ...s, key });
    notes.push(...requirementNotes(key, pred));
  }
  const neverPass = CLIENT_PLATFORMS.filter((p) => shortfalls.some((s) => s.platform === p));
  return { shortfalls, notes, neverPass };
}

/** "macOS and iOS", "macOS, iOS and Android". */
export function platformList(platforms: ClientPlatform[]): string {
  const names = platforms.map((p) => PLATFORM_LABEL[p]);
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

const OP_SYMBOL: Record<string, string> = {
  eq: '=', neq: '≠', in: '∈', not_in: '∉', gte: '≥', lte: '≤', gt: '>', lt: '<',
};

/** Render a requirement as `Label <op> value(s)` for chips and tooltips. */
export function fmtRequirement(key: string, pred: PosturePredicate): string {
  const label = postureAttribute(key)?.label ?? key;
  if (typeof pred === 'string') return `${label} = ${pred}`;
  return `${label} ${OP_SYMBOL[pred.op] ?? pred.op} ${(pred.values ?? []).join(', ')}`;
}
